import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { filter, find, flatMap, isEqual, map, size, uniqBy } from 'lodash';

import { buildCaseOrganizationRequestPayload } from './dialogState';

export type CaseOrganizationExecutionMode = 'routine' | 'selectedCaseOverride' | 'wholeStudyReplacement';

export const caseOrganizationHandlingLabels: Record<CaseOrganizationExecutionMode, string> = {
  routine: 'Add to existing cases',
  selectedCaseOverride: 'Replace slides in listed cases',
  wholeStudyReplacement: 'Replace all case assignments in this study',
};

export const caseOrganizationSubmissionLabels: Record<CaseOrganizationExecutionMode, string> = {
  routine: 'Add and edit listed cases',
  selectedCaseOverride: 'Replace slides in listed cases',
  wholeStudyReplacement: 'Replace all case assignments',
};

export interface BoundedCaseOrganizationDetails<T> {
  count: number;
  items: T[];
  omittedCount: number;
}

export interface CaseOrganizationCaseChange {
  caseId?: number;
  caseLabel: string;
  slideCount: number;
}

export interface CaseOrganizationSlideAssignment {
  slideId: string;
  slideLabel: string;
  targetCaseLabel: string;
}

export interface CaseOrganizationSlideMove extends CaseOrganizationSlideAssignment {
  sourceCaseLabel: string;
}

export interface CaseOrganizationCrossScopeAssignment extends CaseOrganizationSlideAssignment {
  sourceLabId?: string;
  sourceLabName?: string;
  sourceStudyId?: string;
  targetLabId: string;
  targetStudyId: string;
}

export interface CaseOrganizationPlan {
  executionMode: CaseOrganizationExecutionMode;
  requestPayload: Procedure[];
  createdCases: BoundedCaseOrganizationDetails<CaseOrganizationCaseChange>;
  updatedCases: BoundedCaseOrganizationDetails<CaseOrganizationCaseChange>;
  assignedSlides: BoundedCaseOrganizationDetails<CaseOrganizationSlideAssignment>;
  movedSlides: BoundedCaseOrganizationDetails<CaseOrganizationSlideMove>;
  emptiedCases: BoundedCaseOrganizationDetails<CaseOrganizationCaseChange>;
  crossScopeSlides: BoundedCaseOrganizationDetails<CaseOrganizationCrossScopeAssignment>;
  replacedCases: BoundedCaseOrganizationDetails<CaseOrganizationCaseChange>;
  replacedSlidesCount: number;
  isNoOp: boolean;
}

interface BuildCaseOrganizationPlanInput {
  canonicalDraft: Procedure[];
  existingCases: Procedure[];
  executionMode?: CaseOrganizationExecutionMode;
  targetLabId: string;
  targetStudyId: string;
  detailLimit?: number;
}

interface SlideWithCase {
  caseLabel: string;
  slide: Slide;
}

const DEFAULT_DETAIL_LIMIT = 10;

const toBoundedDetails = <T>(items: T[], detailLimit: number): BoundedCaseOrganizationDetails<T> => ({
  count: size(items),
  items: items.slice(0, detailLimit),
  omittedCount: Math.max(0, size(items) - detailLimit),
});

const getCaseLabel = (procedure: Procedure) => procedure.label || '';

const toCaseChange = (procedure: Procedure): CaseOrganizationCaseChange => ({
  caseId: procedure.id,
  caseLabel: getCaseLabel(procedure),
  slideCount: size(procedure.slides),
});

const getSlidesWithCases = (procedures: Procedure[]): SlideWithCase[] =>
  flatMap(procedures, (procedure) => map(procedure.slides, (slide) => ({ caseLabel: getCaseLabel(procedure), slide })));

export const buildCaseOrganizationPlan = ({
  canonicalDraft,
  existingCases,
  executionMode = 'routine',
  targetLabId,
  targetStudyId,
  detailLimit = DEFAULT_DETAIL_LIMIT,
}: BuildCaseOrganizationPlanInput): CaseOrganizationPlan => {
  const requestPayload = buildCaseOrganizationRequestPayload({ procedures: canonicalDraft, studyId: targetStudyId });
  const existingPayload = buildCaseOrganizationRequestPayload({ procedures: existingCases, studyId: targetStudyId });
  const existingSlidesWithCases = getSlidesWithCases(existingCases);
  const draftSlidesWithCases = getSlidesWithCases(canonicalDraft);

  const createdCaseProcedures = filter(
    requestPayload,
    (draftCase) => !find(existingCases, (existingCase) => getCaseLabel(existingCase) === getCaseLabel(draftCase))
  );
  const updatedCaseProcedures = filter(requestPayload, (draftCase) => {
    const matchingExistingCase = find(
      existingPayload,
      (existingCase) => getCaseLabel(existingCase) === getCaseLabel(draftCase)
    );
    return Boolean(matchingExistingCase) && !isEqual(draftCase, matchingExistingCase);
  });
  const assignedSlides = filter(
    draftSlidesWithCases,
    ({ slide }) => !find(existingSlidesWithCases, ['slide.id', slide.id])
  );
  const movedSlides = filter(draftSlidesWithCases, ({ caseLabel, slide }) => {
    const existingSlide = find(existingSlidesWithCases, ['slide.id', slide.id]);
    return Boolean(existingSlide) && existingSlide?.caseLabel !== caseLabel;
  });
  const movedSlideIds = map(movedSlides, 'slide.id');
  const emptiedCaseProcedures = filter(existingCases, (existingCase) => {
    const existingSlideIds = map(existingCase.slides, 'id');
    return existingSlideIds.length > 0 && existingSlideIds.every((slideId) => movedSlideIds.includes(slideId));
  });
  const crossScopeSlides = filter(draftSlidesWithCases, ({ slide }) => {
    const sourceStudyId = slide.sourceStudyId || slide.studyId;
    return (
      (Boolean(slide.labId) && slide.labId !== targetLabId) ||
      (Boolean(sourceStudyId) && sourceStudyId !== targetStudyId)
    );
  });
  const plannedCaseLabels = new Set(map(requestPayload, getCaseLabel));
  const replacedCaseProcedures =
    executionMode === 'wholeStudyReplacement'
      ? filter(existingCases, (existingCase) => !plannedCaseLabels.has(getCaseLabel(existingCase)))
      : [];

  const createdCases = map(createdCaseProcedures, toCaseChange);
  const updatedCases = map(updatedCaseProcedures, toCaseChange);
  const slideAssignments = map(assignedSlides, ({ caseLabel, slide }) => ({
    slideId: slide.id,
    slideLabel: slide.originalFileName || slide.id,
    targetCaseLabel: caseLabel,
  }));
  const slideMoves = map(movedSlides, ({ caseLabel, slide }) => ({
    slideId: slide.id,
    slideLabel: slide.originalFileName || slide.id,
    sourceCaseLabel: find(existingSlidesWithCases, ['slide.id', slide.id])?.caseLabel || '',
    targetCaseLabel: caseLabel,
  }));
  const scopeChanges = map(crossScopeSlides, ({ caseLabel, slide }) => ({
    slideId: slide.id,
    slideLabel: slide.originalFileName || slide.id,
    sourceLabId: slide.labId,
    sourceLabName: slide.sourceLabName,
    sourceStudyId: slide.sourceStudyId || slide.studyId,
    targetCaseLabel: caseLabel,
    targetLabId,
    targetStudyId,
  }));

  const hasChanges =
    createdCases.length > 0 ||
    updatedCases.length > 0 ||
    slideAssignments.length > 0 ||
    slideMoves.length > 0 ||
    emptiedCaseProcedures.length > 0 ||
    replacedCaseProcedures.length > 0;

  return {
    executionMode,
    requestPayload,
    createdCases: toBoundedDetails(createdCases, detailLimit),
    updatedCases: toBoundedDetails(updatedCases, detailLimit),
    assignedSlides: toBoundedDetails(slideAssignments, detailLimit),
    movedSlides: toBoundedDetails(slideMoves, detailLimit),
    emptiedCases: toBoundedDetails(map(emptiedCaseProcedures, toCaseChange), detailLimit),
    crossScopeSlides: toBoundedDetails(scopeChanges, detailLimit),
    replacedCases: toBoundedDetails(map(uniqBy(replacedCaseProcedures, 'id'), toCaseChange), detailLimit),
    replacedSlidesCount: size(flatMap(replacedCaseProcedures, 'slides')),
    isNoOp: !hasChanges,
  };
};
